import type { TFunction } from "i18next";
import { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { type LogicalUnitAction } from "../../../../U2101Service";
import { useState } from "react";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";

// Gestion des handlers pour les actions d'une unité logique
export interface U2101LogicalUnitActionHandler {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<LogicalUnitAction>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<LogicalUnitAction>>;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U2101LogicalUnitActionHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<LogicalUnitAction>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<LogicalUnitAction>>;
}

// Gestion des handlers pour les actions d'une unité logique
export interface U2101LogicalUnitActionHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;

  onDlgClose: (result: string) => void;

  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;

  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<LogicalUnitAction>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<LogicalUnitAction>>;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les actions d'une unité logique
export function u2101LogicalUnitActionHandler(params: U2101LogicalUnitActionHandlerParam): U2101LogicalUnitActionHandlerReturn {
  const { gridStateHandlers, formStateHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  // Gestion de l'ajout d'une action d'unité logique
  const onAdd = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
    return;
  };

  // Gestion de la modification d'une action d'unité logique
  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as LogicalUnitAction);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
    return;
  };

  // Gestion de la suppression d'une action d'unité logique
  const onDelete = (): void => {
    setDeleteDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      // Valider les données avant de sauvegarder
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers && businessHandlers.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        // Si des erreurs de validation existent, ne pas fermer le dialogue
        if (Object.keys(errors).length > 0) {
          return; // Ne pas fermer le dialogue si des erreurs de validation existent
        }
      }
      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        // important: mettre à jour la ligne dans le gridStateHandlers && mette à jour idChecked à false
        const updatedData = gridStateHandlers.getData().map((item) => (item.id === formStateHandlers.getData().id ? { ...formStateHandlers.getData(), idChecked: false } : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent(dataChangedEvent + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        const newData = formStateHandlers.getData();

        // Générer un UUID temporaire pour les nouveaux enregistrements
        newData.id = generateTemporaryId();
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent(dataChangedEvent + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
    return;
  };

  // Gestion de la fermeture du dialog de suppression
  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);

      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return { onAdd, onModify, onDelete, onDlgClose, isDialogOpen, setDialogOpen, formStateHandlers, gridStateHandlers, isDeleteDialogOpen, onDlgDeleteClose, dataChangedEvent };
}
